/**
 * @file RemoveAcademyDialogContainer.tsx
 * @description Container for the Remove Academy confirmation dialog.
 * Calls setPlayerAcademyStatus(isAcademy: false) on confirm.
 *
 * Renders the shared ConfirmDialog molecule (SE-5524). The
 * legacy View had no testIDs; the molecule's `${testID}-cancel|confirm`
 * derivation now provides them from the `remove-academy-dialog` root.
 * @module features/squad-planning/components/RemoveAcademyDialog
 */

import React, { memo, useCallback } from "react";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";
import { useSquadPlanningSetPlayerAcademyStatusMutation } from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";

/** Props for {@link RemoveAcademyDialogContainer} */
interface RemoveAcademyDialogContainerProps {
  /** Whether the dialog is visible */
  readonly isOpen: boolean;
  /** ID of the player to remove from Academy */
  readonly playerId: string | null;
  /** Name of the player for display */
  readonly playerName: string;
  /** Callback to close the dialog */
  readonly onClose: () => void;
}

/**
 * Container that manages the setPlayerAcademyStatus mutation
 * for removing a player's Academy tag.
 * @param props - Component props
 * @param props.isOpen - Whether the dialog is visible
 * @param props.playerId - ID of the player to remove
 * @param props.playerName - Player name for display
 * @param props.onClose - Close callback
 * @returns The remove academy confirmation dialog
 */
const RemoveAcademyDialogContainer = ({
  isOpen,
  playerId,
  playerName,
  onClose,
}: RemoveAcademyDialogContainerProps) => {
  const toast = useToastNotification();

  const [setAcademyStatus, { loading }] =
    useSquadPlanningSetPlayerAcademyStatusMutation({
      refetchQueries: ["SquadPlanningGetSquadPlayersByPosition"],
      onCompleted: () => {
        toast.show({
          action: "success",
          description: `${playerName} removed from Academy`,
        });
        onClose();
      },
      onError: (error: { message: string }) => {
        toast.show({
          action: "danger",
          description: error.message || "Failed to remove player from Academy",
        });
      },
    });

  const handleConfirm = useCallback(() => {
    if (playerId == null) return;
    void setAcademyStatus({
      variables: { input: { playerId, isAcademy: false } },
    });
  }, [playerId, setAcademyStatus]);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Remove Academy Player"
      body={`Remove ${playerName} as an Academy player? This won't delete them from the global player database.`}
      tone="danger"
      confirmLabel={loading ? "Removing..." : "Remove"}
      isConfirming={loading}
      testID="remove-academy-dialog"
    />
  );
};

RemoveAcademyDialogContainer.displayName = "RemoveAcademyDialogContainer";

export default memo(RemoveAcademyDialogContainer);
